import type {
  BillingPrime,
  MissionPrime,
  MonthlyTamPrime,
  BasePrime,
  IBloomies,
  BloomieTamSeniority,
  PrimeRegularisation,
} from '@bloomays-lib/types.shared';

/** Champs affichés dans le DataGridV2 (TEC-2805). */
type TamPrimeTableField =
  | 'jobFunnelProjectId'
  | 'airtableId'
  | 'errorCode'
  | 'clientSocietyName'
  | 'bloomerFullName'
  | 'jobType'
  | 'monthlyMargin'
  | 'monthlyPrime'
  | 'splitRate'
  | 'commissionRate'
  | 'split';

type MissionPrimeCraField =
  | 'craStatus'
  | 'normalWorkedDays'
  | 'saturdaysWorkedDays'
  | 'sundayAndholidaysWorkedDays'
  | 'astreinteWorkedDays';

export type MissionPrimeLine = Pick<MissionPrime, TamPrimeTableField | MissionPrimeCraField> & {
  __typename?: 'MissionPrime';
};

export type BillingPrimeLine = Pick<BillingPrime, TamPrimeTableField | 'paymentTerms'> & {
  __typename?: 'BillingPrime';
};

export type TamPrimeLine = MissionPrimeLine | BillingPrimeLine;

type MonthlyTamPrimeAggregateField =
  | 'bonus'
  | 'regularisation'
  | 'totalFreelanceMarge'
  | 'totalBillingMarge'
  | 'totalMarge'
  | 'totalPrime'
  | 'totalDeals'
  | 'totalMissions';

type MonthlyTamPrimeAggregateBase = Pick<MonthlyTamPrime, MonthlyTamPrimeAggregateField> & {
  primes: TamPrimeLine[];
};

/** Réponse pending `getMonthlyTamPrime`. */
export type MonthlyTamPrimeAggregate = MonthlyTamPrimeAggregateBase & {
  __typename?: 'MonthlyTamPrime';
};

/** Réponse validée `getMonthlyTamPrime`. */
export type ValidatedTamPrimeAggregate = MonthlyTamPrimeAggregateBase & {
  __typename: 'ValidatedTamPrime';
  id: string;
  monthYear: string;
  validationDate: string;
};

export type MonthlyTamPrimeQueryResult = MonthlyTamPrimeAggregate | ValidatedTamPrimeAggregate;

export type GetMonthlyTamPrimeQueryData = {
  getMonthlyTamPrime: MonthlyTamPrimeQueryResult;
};

type PrimeRegularisationQueryField =
  | 'id'
  | 'tam'
  | 'monthYear'
  | 'total'
  | 'status'
  | 'createdBy'
  | 'modifiedBy'
  | 'createdAt'
  | 'modifiedAt';

export type PrimeRegularisationQueryRow = Pick<PrimeRegularisation, PrimeRegularisationQueryField> & {
  createdAt: string;
  modifiedAt: string | null;
};

export type GetPrimeRegularisationByTamAndMonthQueryData = {
  getPrimeRegularisationByTamAndMonth: PrimeRegularisationQueryRow | null;
};

export type MonthlyPrimeHeaderAmounts = {
  freelanceMarge: number;
  billingMarge: number;
  commission: number;
  bonus: number;
  regularisation: number;
  total: number;
};

export type MissionPrimeAdditionalDataInput = Pick<
  MissionPrime,
  'normalWorkedDays' | 'saturdaysWorkedDays' | 'sundayAndholidaysWorkedDays' | 'astreinteWorkedDays'
>;

export type MonthlyPrimeLineValidationInput = Omit<
  BasePrime,
  'clientSocietyName' | 'bloomerFullName' | 'jobType' | 'airtableId' | 'errorCode' | 'split'
> & {
  additionalData?: MissionPrimeAdditionalDataInput;
};

/** Payload mutation `validateMonthlyPrime` (miroir contrat GQL TEC-2806). */
export type MonthlyTamPrimeValidationInput = Pick<MonthlyTamPrime, MonthlyTamPrimeAggregateField> & {
  primes: MonthlyPrimeLineValidationInput[];
};

export const isValidatedMonthlyTamPrime = (
  aggregate: MonthlyTamPrimeQueryResult,
): aggregate is ValidatedTamPrimeAggregate => aggregate.__typename === 'ValidatedTamPrime';

type MonthlyTamPrimeSummaryField =
  | 'bonus'
  | 'regularisation'
  | 'totalFreelanceMarge'
  | 'totalBillingMarge'
  | 'totalMarge'
  | 'totalPrime'
  | 'totalDeals'
  | 'totalMissions';

/** Prime validée dans l'agrégat annuel — inclut lignes (`GET_TAM_PRIMES`, TEC-2818). */
export type ValidatedTamPrimeWithLines = Pick<MonthlyTamPrime, MonthlyTamPrimeSummaryField> & {
  id: string;
  monthYear: string;
  validationDate: string;
  primes: TamPrimeLine[];
};

/** État route passé au clic loupe depuis le dashboard annuel (FR-004/FR-005). */
export type PrimesMonthlyNavigationState = {
  preloadedMonthlyTamPrime: MonthlyTamPrimeQueryResult;
};

export type MonthlyPrimeNavigationTarget = {
  pathname: string;
  search: string;
  state: PrimesMonthlyNavigationState;
};

export type AnnualPrimeRowStatus = 'validated' | 'pending';

export type AnnualPrimeTableRow = Pick<MonthlyTamPrime, MonthlyTamPrimeSummaryField> & {
  rowId: string;
  monthYear: string;
  status: AnnualPrimeRowStatus;
};

export type AnnualTamPrimeAggregate = {
  tam: string;
  tamSeniority: BloomieTamSeniority;
  goalCompletion: number;
  validatedPrimes: ValidatedTamPrimeWithLines[];
  pendingPrimes: MonthlyTamPrimeAggregate | null;
};

export type GetTamPrimesQueryData = {
  getTamPrimes: AnnualTamPrimeAggregate;
};

/** TAM actif renvoyé par `GET_ACTIVE_TAMS` — sous-ensemble de {@link IBloomies}. */
export type ActiveTam = Required<Pick<IBloomies, 'email' | 'firstname' | 'lastname' | 'seniority' | 'luccaId'>>;

/** Réponse Apollo `GET_ACTIVE_TAMS` (TEC-2815). */
export type GetActiveTamsQueryData = {
  getActiveTams: ActiveTam[];
};

export type MonthIndex = '01' | '02' | '03' | '04' | '05' | '06' | '07' | '08' | '09' | '10' | '11' | '12';

export type MonthlySynthesisCellStatus = 'loading' | 'error' | 'empty' | 'value';

export type MonthlySynthesisValueSource = 'validated' | 'pending';

export type MonthlySynthesisCell = {
  status: MonthlySynthesisCellStatus;
  totalMarge: number | null;
  totalPrime: number | null;
  /** Présent lorsque status === 'value' — pilote la couleur de cellule (TEC-3066). */
  valueSource: MonthlySynthesisValueSource | null;
};

export type CLevelSynthesisViewMode = 'marge' | 'prime';

export const CLEVEL_SYNTHESIS_EXPORT_FOOTER_ROW_ID = '__clevel-synthesis-export__' as const;

export type CLevelSynthesisExportFooterRow = {
  rowId: typeof CLEVEL_SYNTHESIS_EXPORT_FOOTER_ROW_ID;
  rowKind: 'export-footer';
};

export type CLevelMonthExportCellState = {
  monthIndex: MonthIndex;
  enabled: boolean;
};

export type CLevelPrimesDashboardGridRow = CLevelSynthesisRow | CLevelSynthesisExportFooterRow;

export type CLevelSynthesisRowStatus = 'loading' | 'success' | 'error';

/** Ligne pivot DataGrid synthèse C-Level (état UI). */
export type CLevelSynthesisRow = {
  rowId: string;
  tamEmail: string;
  tamDisplayName: string;
  tamFirstname: string;
  tamLastname: string;
  luccaId: string | null;
  rowStatus: CLevelSynthesisRowStatus;
  months: Record<MonthIndex, MonthlySynthesisCell>;
  annualTamPrime?: AnnualTamPrimeAggregate;
};
